import React, { createContext, useContext, type ReactNode } from 'react';
import type { ErrorLike } from '@apollo/client';
import { useThemeSwitcher } from '@gastrosoft/shared/theme';
import { useOrganizationSelection, type CurrentTenantInfo } from '@gastrosoft/shared/contexts';
import {
  GetOrganizationsForAddressListDocument,
  GetCurrentTenantDocument,
  type GetOrganizationsForAddressListQuery,
  type GetOrganizationsForAddressListQueryVariables,
  type GetCurrentTenantQuery,
} from '@/generated/root-types';
import { GRAPHQL_MAX_PAGE_SIZE } from '@/utils/constants';

/** The reseller's own organization(s), i.e. `organizations(tenantId: <own tenantId>)`. */
type Organization = NonNullable<
  NonNullable<GetOrganizationsForAddressListQuery['organizations']>['nodes']
>[number];

interface OrganizationContextType {
  currentOrganization: Organization | null;
  organizations: Organization[];
  setCurrentOrganization: (org: Organization) => void;
  loading: boolean;
  currentTenantId: string | undefined;
  organizationsQueryData: GetOrganizationsForAddressListQuery | undefined;
  organizationsQueryError: ErrorLike | undefined;
  tenantQueryError: ErrorLike | undefined;
  refetchOrganizations: () => Promise<unknown>;
  isResellerTenant: boolean;
  hasResellerAccess: boolean;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};

const mapOrganizations = (data: GetOrganizationsForAddressListQuery | undefined): Organization[] =>
  data?.organizations?.nodes?.filter((org): org is NonNullable<typeof org> => org != null) ?? [];

const mapCurrentTenant = (
  data: GetCurrentTenantQuery | undefined,
): CurrentTenantInfo | undefined =>
  data?.currentTenant
    ? {
        id: data.currentTenant.id,
        type: data.currentTenant.type,
        resellerId: data.currentTenant.resellerId,
      }
    : undefined;

interface OrganizationProviderProps {
  children: ReactNode;
}

export const OrganizationProvider: React.FC<OrganizationProviderProps> = ({ children }) => {
  const { currentBrand } = useThemeSwitcher();

  const value = useOrganizationSelection<
    Organization,
    GetOrganizationsForAddressListQuery,
    GetOrganizationsForAddressListQueryVariables,
    GetCurrentTenantQuery
  >({
    organizationsDocument: GetOrganizationsForAddressListDocument,
    currentTenantDocument: GetCurrentTenantDocument,
    getOrganizationsVariables: (tenantId) => ({ first: GRAPHQL_MAX_PAGE_SIZE, tenantId }),
    mapOrganizations,
    mapCurrentTenant,
    enabled: currentBrand === 'orda-pos',
  });

  return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>;
};
